import { EMAIL_MAX_LENGTH } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogClose, DialogContent, Field, Icon, Input, Textarea, toast } from "@sagot/ui";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useErrorMessage } from "../../../lib/api-errors";
import { zodResolver } from "../../../lib/zod-resolver";
import { useEmailInstructions } from "./use-widget-studio";

const NOTE_MAX = 500;
const schema = z.object({ email: z.string().trim().email().max(EMAIL_MAX_LENGTH), note: z.string().trim().max(NOTE_MAX) });
type EmailForm = z.infer<typeof schema>;
const K = "deployKnowledge.widgetStudio.emailDialog";

interface EmailDevDialogProps {
  agentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** `POST /v1/agents/{id}/widget/install-email` — snippet + Agent ID + guide; never the secret. */
export function EmailDevDialog({ agentId, open, onOpenChange }: EmailDevDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const send = useEmailInstructions(agentId);
  const form = useForm<EmailForm>({ resolver: zodResolver(schema), defaultValues: { email: "", note: "" } });
  const submit = form.handleSubmit((values) => {
    send.mutate(values, {
      onSuccess: () => {
        toast.success(t(`${K}.sent`, { email: values.email }));
        form.reset();
        onOpenChange(false);
      },
      onError: (error) => toast.error(message(error)),
    });
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t(`${K}.title`)} closeLabel={t("common.close")} className="max-w-md">
        <form className="space-y-4" onSubmit={(e) => void submit(e)} noValidate>
          <Field label={t(`${K}.email`)} error={form.formState.errors.email ? t(`${K}.emailInvalid`) : undefined}>
            <Input type="email" autoComplete="email" maxLength={EMAIL_MAX_LENGTH} {...form.register("email")} />
          </Field>
          <Field label={t(`${K}.note`)}>
            <Textarea rows={3} maxLength={NOTE_MAX} {...form.register("note")} />
          </Field>
          <p className="text-xs text-fg-subtle">{t(`${K}.includes`)}</p>
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button variant="ghost">{t("common.cancel")}</Button>
            </DialogClose>
            <Button type="submit" loading={send.isPending}>
              <Icon name="send" />
              {t(`${K}.send`)}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
